import React, { useState } from 'react';
import type { FormikValues } from 'formik';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import type { AdditionalInformationSection } from '~/components/types/incident-configuration-types';

import copies from '../../copies.json';
import { buildCategoryLimitOptions, usePolicyCategoryOptions } from '../../PolicyCategoriesOptions';

import AddCustomSectionDialog from './AddCustomSectionDialog';
import {
  ADDITIONAL_INFORMATION_MAX_SECTIONS,
  ADDITIONAL_INFORMATION_SECTIONS_PATH,
  countActiveSections,
} from './constants';

const TEXTS = copies.additional_information;

const AddSectionPanel = (): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // `?? []` not the lodash default: the field is nullable and `get` defaults only for `undefined`, so a
  // stored JSON `null` would otherwise crash the `.map`/`.length` below.
  const sections: AdditionalInformationSection[] = get(values, ADDITIONAL_INFORMATION_SECTIONS_PATH) ?? [];
  const existingKeys = sections.map((section) => section.id);
  const atCap = countActiveSections(sections) >= ADDITIONAL_INFORMATION_MAX_SECTIONS;

  const policyCategoryOptions = usePolicyCategoryOptions();
  const incidentCategories =
    (get(values, 'configuration.categories', []) as { id: string; desc: string }[])?.map((category) => ({
      id: category.id,
      value: category.id,
      label: category.desc,
    })) ?? [];
  // The new-section dialog offers the same grouped list as the section it creates.
  const categories = buildCategoryLimitOptions(incidentCategories, policyCategoryOptions);

  const closeDialog = () => setIsDialogOpen(false);

  const handleAddSection = (section: AdditionalInformationSection) => {
    setFieldValue(ADDITIONAL_INFORMATION_SECTIONS_PATH, [...sections, section]);
    closeDialog();
  };

  const addButton = (
    // MUI restores focus to whatever opened a dialog, which is what returns a keyboard user to their
    // place in the page -- so nothing here blurs the trigger on close. What the restore also brings is
    // MUI's PULSATING focus ripple: a filled block at 0.3 opacity that on a text button reads as a
    // stuck selection, and appears whenever the dialog was dismissed by keyboard (including Enter
    // submitting the form, the commonest way to add a section).
    //
    // `ripplePulsate` is targeted rather than the whole `MuiTouchRipple-root`, so only the focus ripple
    // goes: a click ripple is `rippleVisible` WITHOUT `ripplePulsate`, and hiding the root would have
    // taken it too whenever the button was already focus-visible (tabbed to, then clicked), because
    // `Mui-focusVisible` survives mousedown.
    //
    // `teal-700` not `teal-500`: at 2px on a white surface teal-500 (#5BD2D5) is 1.81:1, under WCAG
    // 2.1 SC 1.4.11's 3:1 for a non-text indicator, and with `outline-none` this ring is the button's
    // ONLY focus affordance -- it has no focus background, box-shadow or outline of its own. teal-700
    // (#1A9C9E) is 3.34:1 and is already the colour of this button's own label in the theme.
    //
    // Scoped to this one button on purpose; an app-wide MuiButton override was considered and rejected
    // as too broad for a cosmetic problem on one screen.
    <Button
      variant="text"
      color="primary"
      className="outline-none focus-visible:ring-2 focus-visible:ring-teal-700 [&.Mui-focusVisible_.MuiTouchRipple-ripplePulsate]:hidden"
      onClick={() => setIsDialogOpen(true)}
      disabled={atCap}
    >
      <AddIcon />
      {TEXTS.add_section}
    </Button>
  );

  return (
    <div className="flex">
      {atCap ? (
        <FsTooltip
          arrow
          title={TEXTS.add_section_at_cap_tooltip.replace('{max}', String(ADDITIONAL_INFORMATION_MAX_SECTIONS))}
        >
          {/* span keeps the tooltip working over a disabled button (which swallows pointer events) */}
          <span>{addButton}</span>
        </FsTooltip>
      ) : (
        addButton
      )}
      <AddCustomSectionDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onSubmit={handleAddSection}
        existingKeys={existingKeys}
        categories={categories}
      />
    </div>
  );
};

export default AddSectionPanel;
